
import { useCallback } from 'react';
import { MessageType } from '@/types/assistant';
import { 
  StreamingStartEvent, 
  StreamingCompleteEvent 
} from '@/services/UnifiedStreamingService';

interface UseStreamingMessageHandlerProps {
  addAiMessage: (message: MessageType, autoSave?: boolean) => Promise<void>;
  updateMessage: (messageId: string, updates: Partial<MessageType>) => void;
  sessionId: string | null;
  userId: string | null;
  onStreamingError: (error: string, requestId: string) => void;
  setIsTyping: (typing: boolean) => void;
}

interface UseStreamingMessageHandlerReturn {
  handleStreamingStart: (data: StreamingStartEvent) => void;
  handleStreamingContent: (chunk: string, fullContent: string, requestId?: string) => void;
  handleStreamingComplete: (data: StreamingCompleteEvent) => void;
  handleStreamingError: (error: string, requestId?: string) => void;
}

export const useStreamingMessageHandler = ({
  addAiMessage,
  updateMessage,
  sessionId,
  userId,
  onStreamingError,
  setIsTyping
}: UseStreamingMessageHandlerProps): UseStreamingMessageHandlerReturn => {

  const handleStreamingStart = useCallback((data: StreamingStartEvent) => {
    // Reduced logging for performance
    if (process.env.NODE_ENV === 'development') {
      console.log('[STREAMING HANDLER OPTIMIZED] Starting stream:', {
        requestId: data.requestId,
        model: data.model
      });
    }

    if (!sessionId || !userId) {
      console.error('[STREAMING HANDLER OPTIMIZED] Missing session or user ID');
      return;
    }

    setIsTyping(true);

    // Streamlined message creation - minimal metadata
    const initialMessage: MessageType = {
      id: data.requestId,
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      session_id: sessionId,
      user_id: userId,
      suggestions: [],
      is_streaming: true,
      stream_content: '',
      stream_timestamp: new Date(),
      model: data.model || 'gpt-4',
      feedback: null,
      metadata: {
        requestId: data.requestId,
        startTime: Date.now(),
        streaming: true
      }
    };

    // Backend handles persistence - no frontend save
    addAiMessage(initialMessage, false);
  }, [addAiMessage, sessionId, userId, setIsTyping]);

  const handleStreamingContent = useCallback((chunk: string, fullContent: string, requestId?: string) => {
    if (!requestId) return;

    // Direct UI update - no extra logging in production
    updateMessage(requestId, {
      stream_content: fullContent,
      stream_timestamp: new Date(),
      is_streaming: true,
      metadata: {
        lastUpdate: Date.now(),
        streaming: true,
        requestId
      }
    });
  }, [updateMessage]);

  const handleStreamingComplete = useCallback(async (data: StreamingCompleteEvent) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[STREAMING HANDLER OPTIMIZED] Completing stream:', {
        requestId: data.requestId,
        contentLength: data.content?.length || 0
      });
    }

    setIsTyping(false);

    // Streamlined completion - essential data only
    const finalUpdates: Partial<MessageType> = {
      content: data.content || '',
      suggestions: data.suggestions || [],
      is_streaming: false,
      stream_content: data.content || '',
      stream_timestamp: new Date(),
      metadata: {
        knowledgeUsed: data.knowledgeUsed || false,
        knowledgeResults: data.knowledgeResults || [],
        suggestionsSource: data.suggestionsSource,
        contextTruncated: data.contextTruncated || false,
        queryExpansionUsed: data.queryExpansionUsed || false,
        expandedQueries: data.expandedQueries || [],
        usage: data.usage || {
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: 0
        },
        completedAt: Date.now(),
        streaming: false,
        requestId: data.requestId
      }
    };

    // UI-only update - backend handles persistence
    updateMessage(data.requestId, finalUpdates);
  }, [updateMessage, setIsTyping]);

  const handleStreamingError = useCallback((error: string, requestId?: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('[STREAMING HANDLER OPTIMIZED] Stream error:', { error, requestId });
    }
    
    setIsTyping(false);
    
    if (requestId) {
      updateMessage(requestId, {
        is_streaming: false,
        stream_timestamp: new Date(),
        metadata: {
          error: true,
          errorMessage: error,
          streaming: false,
          requestId
        }
      });
    }
    
    onStreamingError(error, requestId || '');
  }, [onStreamingError, setIsTyping, updateMessage]);

  return {
    handleStreamingStart,
    handleStreamingContent,
    handleStreamingComplete,
    handleStreamingError
  };
};
